"use client";

import React, { Fragment, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { formatPeriodoTorneioBR } from "../../utils/date";
import Header from "../../components/Header";
import Leftnav from "../../components/Leftnav";
import ProfileCardTournament from "../../components/ProfilecardTournament";
import Load from "../../components/Load";
import CreditCardModal, { CreditCardInfo } from "../../components/CreditCard";
import PixModal from "../../components/PixModal";
import { usePagamentoComStatusContaIugu } from "../../hooks/usePagamentoComStatusContaIugu";
import {
  aplicarCupomPagamento,
  retirarCupomPagamento,
  finalizarPagamentoInscricaoService,
  MeioPagamento,
  PagamentoPixInfo,
} from "../../services/pagamentoInscricaoService";

const schema = z.object({
    cep: z.string().regex(/^[0-9]{5}-?[0-9]{3}$/, 'CEP inválido'),
    estado: z.string().min(1, 'Campo obrigatório'),
    cidade: z.string().min(1, 'Campo obrigatório'),
    bairro: z.string().min(1, 'Campo obrigatório'),
    endereco: z.string().min(1, 'Campo obrigatório'),
    numero: z.string().min(1, 'Campo obrigatório'),
    complemento: z.string().optional(),
    paymentMethod: z.enum(['pix', 'credit'], {
        required_error: 'Selecione a forma de pagamento',
    }),
});

type FormData = z.infer<typeof schema>;

const NewInscriptionPayment: React.FC = () => {
    const navigate = useNavigate();
    
    const { state } = useLocation() as { state?: { pagamentoId?: string } };
    const { id: paramId } = useParams<{ id?: string }>();
    const pagamentoId = state?.pagamentoId || paramId || "";

    const { pagamento, inscricoes, ligaContaIuguAtiva, loading, error, refetch } =
        usePagamentoComStatusContaIugu(pagamentoId);

    const [showCreditModal, setShowCreditModal] = useState(false);
    const [showPixModal, setShowPixModal] = useState(false);
    const [pixData, setPixData] = useState<PagamentoPixInfo | null>(null);
    const [pixLoading, setPixLoading] = useState(false);
    const [pixError, setPixError] = useState<string | null>(null);

    const [isPaying, setIsPaying] = useState(false);
    const [formValues, setFormValues] = useState<FormData | null>(null);

    const [cupom, setCupom] = useState("");
    const [cupomMsg, setCupomMsg] = useState<string | null>(null);

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<FormData>({
        resolver: zodResolver(schema),
    });
    const paymentMethod = watch('paymentMethod');

    if (loading) return <Load />;
    if (error) return (
        <div className="container mt-5">
            <div className="alert alert-danger" role="alert">
                Erro ao carregar pagamento.
            </div>
        </div>
    );

    const torneioRaw = inscricoes[0]?.categoriaTorneio.torneio;
    
    const statusPagamento = pagamento?.status || "";
    const isPendente = statusPagamento === "PENDENTE";
    const isExpired = pagamento?.dataVencimento
        ? dayjs().isAfter(dayjs(pagamento.dataVencimento).endOf('day'))
        : false;
    const isDisabled = !isPendente || !ligaContaIuguAtiva || isExpired;

    const gamesBase = pagamento?.valorSemDesconto ?? inscricoes.reduce(
        (sum, c) => sum + (c.categoriaTorneio.precoInscricao ?? 0),
        0
    );
    const desconto = pagamento?.descontoAplicado ?? 0;
    const valorFinal = pagamento?.valor ?? gamesBase;
    const cupomAplicado = pagamento?.cupomAplicado;
    const torneio = torneioRaw
        ? {
            ...torneioRaw,
            local: torneioRaw.hasOwnProperty('local')
                ? (torneioRaw as any).local
                : {
                    endereco: {
                        cidade: { nome: "", uf: "" }
                    }
                },
            dataInicio: torneioRaw.dataInicio ?? "",
            dataFim: torneioRaw.dataFim ?? "",
            bannerUrl: torneioRaw.bannerUrl ?? "",
            avatarUrl: torneioRaw.avatarUrl ?? "",
            subtitulo: (torneioRaw as any).subtitulo ?? ""
        }
        : undefined;

    if (!loading && inscricoes.length === 0) {
        return (
            <div className="container mt-5">
                <p>Nenhuma inscrição encontrada.</p>
                <button className="btn btn-primary" onClick={() => navigate(-1)}>Voltar</button>
            </div>
        );
    }

    const handleAplicarCupom = async () => {
        setCupomMsg(null);
        try {
            await aplicarCupomPagamento(cupom, pagamentoId);
            await refetch();
            setCupom("");
        } catch {
            setCupomMsg("Erro ao aplicar cupom.");
        }
    };

    const handleRemoverCupom = async () => {
        setCupomMsg(null);
        try {
            await retirarCupomPagamento(pagamentoId);
            await refetch();
        } catch {
            setCupomMsg("Erro ao remover cupom.");
        }
    };

    const mapEndereco = (form: FormData) => ({
        cep: form.cep,
        logradouro: form.endereco,
        numero: form.numero,
        bairro: form.bairro,
        cidade: form.cidade,
        uf: form.estado,
        pais: "BR",
        complemento: form.complemento || undefined,
    });

    const onValid = async (form: FormData) => {
        setFormValues(form);
        if (form.paymentMethod === "credit") {
            setShowCreditModal(true);
            return;
        }

        if (form.paymentMethod === "pix") {
            setPixLoading(true);
            setPixError(null);
            setPixData(null);

            try {
                // Mesmo caminho do cartão (finalizarPagamentoInscricao): resolve a liga/split
                // no servidor e persiste o invoiceId no pagamento (necessário p/ o webhook).
                const pagamento = await finalizarPagamentoInscricaoService({
                    pagamentoInscricaoId: pagamentoId,
                    input: {
                        meioPagamento: "PIX" as MeioPagamento,
                        enderecoCobranca: mapEndereco(form),
                    },
                    suppressGlobalError: true,
                });
                const pixInfo = pagamento?.pixInfo;
                if (!pixInfo) {
                    throw new Error("Resposta sem dados do PIX");
                }
                setPixData(pixInfo);
                setShowPixModal(true);
            } catch (error) {
                console.error(error);
                setPixError("Ocorreu um erro ao gerar o pagamento via PIX. Por favor, entre em contato com o suporte da InPlay.");
            } finally {
                setPixLoading(false);
            }
        }
    };

    const handleCreditPay = async (data: CreditCardInfo) => {
        if (!formValues) return;
        try {
            setIsPaying(true);
            await finalizarPagamentoInscricaoService({
                pagamentoInscricaoId: pagamentoId,
                input: {
                    meioPagamento: "CARTAO" as MeioPagamento,
                    enderecoCobranca: mapEndereco(formValues),
                    dadosCartao: {
                        numero: data.cardNumber,
                        nomeImpresso: data.holderName,
                        validadeMes: Number(data.expiryMonth),
                        validadeAno: Number(data.expiryYear) ? 2000 + Number(data.expiryYear) : 0,
                        cvv: data.securityCode,
                    },
                },
            });
            await refetch();
            alert("Pagamento realizado com sucesso!");
            setShowCreditModal(false);
            navigate("/athlete/payments");
        } catch {
            alert("Erro ao processar pagamento.");
        } finally {
            setIsPaying(false);
        }
    };

    const handlePixClose = () => {
        setShowPixModal(false);
        setPixData(null);
    };

    const handleBack = () => {
        navigate(-1);
    };

    const enderecoForm = (
        <div className="card border-0 p-4">
            <h4 className="font-xs ls-1 fw-700 text-grey-900 mb-3">
                Endereço de cobrança
            </h4>
            <div className="row">
                <div className="col-md-6 col-xl-4 col-xxl-4 mb-3 px-1">
                    <label className="font-xssss fw-600 text-grey-700">
                        CEP <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="88888-888"
                        disabled={isDisabled}
                        {...register('cep')}
                    />
                    {errors.cep && <small className="text-danger">{errors.cep.message}</small>}
                </div>
                <div className="col-md-6 col-xl-4 col-xxl-4 mb-3 px-1">
                    <label className="font-xssss fw-600 text-grey-700">
                        Estado <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Estado"
                        disabled={isDisabled}
                        {...register('estado')}
                    />
                    {errors.estado && <small className="text-danger">{errors.estado.message}</small>}
                </div>
                <div className="col-md-6 col-xl-4 col-xxl-4 mb-3 px-1">
                    <label className="font-xssss fw-600 text-grey-700">
                        Cidade <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Cidade"
                        disabled={isDisabled}
                        {...register('cidade')}
                    />
                    {errors.cidade && <small className="text-danger">{errors.cidade.message}</small>}
                </div>
                <div className="col-md-6 col-xl-4 col-xxl-4 mb-3 px-1">
                    <label className="font-xssss fw-600 text-grey-700">
                        Bairro <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Bairro"
                        disabled={isDisabled}
                        {...register('bairro')}
                    />
                    {errors.bairro && <small className="text-danger">{errors.bairro.message}</small>}
                </div>
                <div className="col-md-12 col-xl-9 col-xxl-9 mb-9 px-1">
                    <label className="font-xssss fw-600 text-grey-700">
                        Endereço <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Logradouro (Rua, Avenida, etc.)"
                        disabled={isDisabled}
                        {...register('endereco')}
                    />
                    {errors.endereco && <small className="text-danger">{errors.endereco.message}</small>}
                </div>
                <div className="col-md-6 col-xl-3 col-xxl-3 mb-3 px-1">
                    <label className="font-xssss fw-600 text-grey-700">
                        Número <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Número"
                        disabled={isDisabled}
                        {...register('numero')}
                    />
                    {errors.numero && <small className="text-danger">{errors.numero.message}</small>}
                </div>
                <div className="col-md-12 col-xl-12 col-xxl-12 mb-3 px-1">
                    <label className="font-xssss fw-600 text-grey-700">
                        Complemento / Referência
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Complemento (opcional)"
                        disabled={isDisabled}
                        {...register('complemento')}
                    />
                </div>
                
            </div>
            
        </div>
    );

    return (
        <Fragment>
            <Header />
            <Leftnav />
            <div className="main-content right-chat-active">
                <div className="middle-sidebar-bottom">
                    <div className="middle-sidebar-left pe-0">
                        <div className="row">
                            <div className="col-xl-12 col-xxl-12 col-lg-12">
                                <ProfileCardTournament torneio={torneio} />
                                {!isPendente && (
                                    <div className="alert alert-warning mt-3" role="alert">
                                        <strong>Pagamento {statusPagamento}:</strong> Não é possível continuar com o pagamento. 
                                        {statusPagamento === "PAGO" && "Este pagamento já foi finalizado."}
                                        {statusPagamento === "CANCELADO" && "Este pagamento foi cancelado."}
                                        {statusPagamento === "ESTORNADO" && "Este pagamento foi estornado."}
                                        {!["PAGO", "CANCELADO", "ESTORNADO"].includes(statusPagamento) && "Status não permite alterações."}
                                    </div>
                                )}
                                {isPendente && !ligaContaIuguAtiva && (
                                    <div className="alert alert-info mt-3" role="alert">
                                        Esta Liga não está com uma conta de pagamento ativa.
                                    </div>
                                )}
                                {isPendente && isExpired && (
                                    <div className="alert alert-danger mt-3" role="alert">
                                        <strong>Prazo de pagamento expirado.</strong> Não é possível realizar o pagamento.
                                    </div>
                                )}
                                {isPendente && !isExpired && pagamento?.dataVencimento && (
                                    <div className="alert alert-info mt-3" role="alert">
                                        <i className="bi bi-clock me-1"></i>
                                        Pagamento deve ser realizado até {dayjs(pagamento.dataVencimento).format('DD/MM/YYYY HH:mm')}.
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="row bg-white rounded-xxl shadow-xs p-4 m-1 mb-4">
                            <div className="col-xl-12 col-xxl-12 col-lg-12">
                                <div className="row">
                                    <div className="col-xl-1">
                                        <figure className="avatar w50 me-auto">
                                            <img
                                                src="/assets/images/user.png"
                                                alt="avater"
                                                className="p-1 bg-white rounded-xl w-100"
                                            />
                                        </figure>
                                    </div>
                                    <div className="col-xl-11">
                                        <h5 className="font-xs ls-1 fw-700 text-grey-900">
                                            {torneio?.subtitulo || torneio?.nome}
                                            <span className="d-block font-xssss fw-500 mt-1 lh-3 text-grey-500">
                                                {formatPeriodoTorneioBR(torneio?.dataInicio, torneio?.dataFim)}{' '}
                                                {torneio?.local?.endereco?.cidade && (
                                                    <span className="text-primary">
                                                        {torneio.local.endereco.cidade.nome},{torneio.local.endereco.cidade.uf}
                                                    </span>
                                                )}
                                            </span>
                                        </h5>
                                    </div>
                                    <hr />
                                </div>
                            </div>
                            <form id="inscription-form" onSubmit={handleSubmit(onValid)}>
                                <div className="row mt-4">
                                    <div className="col-lg-6 mb-4">
                                        {enderecoForm}
                                    </div>
                                    <div className="col-lg-6 mb-4">
                                        <div className="card border-0 p-4 h-100 d-flex flex-column">
                                            <h4 className="font-xs ls-1 fw-700 text-grey-900 mb-3">
                                                Detalhes da inscrição
                                            </h4>
                                            <table className="table">
                                                <thead>
                                                    <tr>
                                                        <th>Categoria</th>
                                                        <th className="text-end">Valor</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {inscricoes.map((ins) => (
                                                        <tr key={ins.id}>
                                                            <td>{ins.categoriaTorneio.nome}</td>
                                                            <td className="text-end">
                                                                {(ins.categoriaTorneio.precoInscricao ?? 0).toLocaleString("pt-BR", {
                                                                    style: "currency",
                                                                    currency: "BRL",
                                                                })}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot>
                                                    <tr>
                                                        <th>Subtotal</th>
                                                        <th className="text-end">
                                                            {gamesBase.toLocaleString("pt-BR", {
                                                                style: "currency",
                                                                currency: "BRL",
                                                            })}
                                                        </th>
                                                    </tr>
                                                    <tr>
                                                        <th className="fw-400">Desconto</th>
                                                        <th className="text-end fw-400">
                                                            {(-desconto).toLocaleString("pt-BR", {
                                                                style: "currency",
                                                                currency: "BRL",
                                                            })}
                                                        </th>
                                                    </tr>
                                                    <tr>
                                                        <th>Total</th>
                                                        <th className="text-end">
                                                            {valorFinal.toLocaleString("pt-BR", {
                                                                style: "currency",
                                                                currency: "BRL",
                                                            })}
                                                        </th>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                            <div className="">
                                                {cupomAplicado ? (
                                                    <div className="d-flex align-items-center mb-2">
                                                        <span className="badge bg-info me-2">CUPOM: {cupomAplicado.codigo}</span>
                                                        <button className="btn btn-sm btn-outline-danger" type="button" onClick={handleRemoverCupom} disabled={isDisabled}>Remover</button>
                                                    </div>
                                                ) : (
                                                    <div className="input-group mb-2">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Código do cupom"
                                                            value={cupom}
                                                            disabled={isDisabled}
                                                            onChange={(e) => setCupom(e.target.value)}
                                                        />
                                                        <button className="btn bg-primary text-white" type="button" onClick={handleAplicarCupom} disabled={isDisabled}>
                                                            Aplicar
                                                        </button>
                                                    </div>
                                                )}
                                                {cupomMsg && <small className="text-danger">{cupomMsg}</small>}
                                                <div>
                                                    <div className="form-check my-2">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            id="pix-radio"
                                                            value="pix"
                                                            disabled={isDisabled}
                                                            {...register('paymentMethod')}
                                                        />
                                                        <label className="form-check-label" htmlFor="pix-radio">
                                                            PIX
                                                        </label>
                                                    </div>
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            id="credit-card-radio"
                                                            value="credit"
                                                            disabled={isDisabled}
                                                            {...register('paymentMethod')}
                                                        />
                                                        <label className="form-check-label" htmlFor="credit-card-radio">
                                                            Cartão de Crédito
                                                        </label>
                                                    </div>
                                                    {errors.paymentMethod && (
                                                        <small className="text-danger">{errors.paymentMethod.message}</small>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="d-flex justify-content-between mt-5">
                                                <button
                                                    id="back-button"
                                                    type="button"
                                                    className="btn btn-light"
                                                    onClick={handleBack}
                                                    disabled={isPaying}
                                                >
                                                    Voltar
                                                </button>
                                                <button
                                                    id='next-button'
                                                    type="submit"
                                                    className="btn bg-primary text-white"
                                                    disabled={isPaying || pixLoading || isDisabled}
                                                >
                                                    {pixLoading ? "Gerando PIX..." : "FINALIZAR INSCRICAO"}
                                                </button>
                                                {paymentMethod === "pix" && pixError && <small className="text-danger d-block mt-2">{pixError}</small>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            <CreditCardModal
                open={showCreditModal}
                onClose={() => setShowCreditModal(false)}
                onPay={handleCreditPay}
            />
            <PixModal
                open={showPixModal}
                onClose={handlePixClose}
                pixCode={pixData?.qrcodeText || ""}
                qrCodeUrl={pixData?.qrcodeImageUrl || "/assets/images/qrcodeexample.png"}
            />
        </Fragment>
    );
};

export default NewInscriptionPayment;
